import * as React from 'react';
import cx from 'classnames';
import { GlyphName } from '@remind/r101-glyphs';
import ErrorReporter from 'app/components/ErrorReporter';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
// @ts-ignore
import ImageWrapper from 'components/ImageWrapper';
import Text from 'components/Text';
import { getPropClassesFromInstance } from 'lib/getPropClasses';
import ClickEventTrigger from 'components/ClickEventTrigger';
import Link from 'components/Link';

import './styles/BoxSelectItem.scss';

/**
 * ## An individual [BoxSelect](/internal/components/BoxSelect) item.
 *
 *
 * ```example
 * <BoxSelect>
 *   <BoxSelectItem
 *     glyph="remind-icon"
 *     label="Label"
 *     action={() => console.log('action')}>
 *     BoxSelectItem
 *   </BoxSelectItem>
 *   <BoxSelectItem
 *     glyph="remind-icon"
 *     label="Label"
 *     action={() => console.log('action')}>
 *     BoxSelectItem
 *   </BoxSelectItem>
 * </BoxSelect>
 * ```
 *
 * ```example
 * <BoxSelect>
 *   <BoxSelectItem
 *     glyph="remind-icon"
 *     label="BoxSelectItem"
 *   />
 *   <BoxSelectItem
 *     glyph="remind-icon"
 *     label="BoxSelectItem"
 *   />
 * </BoxSelect>
 * ```
 *
 * ```example
 * <BoxSelect>
 *   <BoxSelectItem
 *     border
 *     glyph="remind-icon"
 *     label="BoxSelectItem"
 *     size="xsmall"
 *     to="https://www.remind.com"
 *   />
 *   <BoxSelectItem
 *     border
 *     glyph="remind-icon"
 *     label="BoxSelectItem"
 *     size="xsmall"
 *     to="https://www.remind.com"
 *   />
 * </BoxSelect>
 * ```
 *
 *
 * # TODO
 * * Fix `glyph` styling
 * * `glyph` is not conditionally rendered
 * * `size` should be `oneOf`
 * * Most props should be moved to [BoxSelect](/internal/components/BoxSelect)
 */

class BoxSelectItem extends React.Component<{
  border?: boolean;
  glyph?: GlyphName;
  icon?: string;
  label?: string;
  size?: string;
  to?: string;
  className?: string;
  eventElementName?: string;
  action?: () => void;
  children?: React.ReactNode;
}> {
  static propClasses = {
    size: {
      xsmall: 'BoxSelectItem--xsmall',
    },
  };

  renderChildren = () => {
    const { children } = this.props;

    return (
      <Text color="graphite" element="div" className="BoxSelectItem-text">
        {children}
      </Text>
    );
  };

  renderIconOrGlyph = () => {
    const { icon, glyph } = this.props;
    if (icon) {
      return <Image src={icon} />;
    } else if (glyph) {
      return <Glyph color="mascot" name={glyph} size="xxxlarge" className="BoxSelectItem-glyph" />;
    }

    return <ErrorReporter error="BoxSelectItem - tried to render without an icon or glyph" />;
  };

  render() {
    const { action, border, children, className, eventElementName, label, to } = this.props;
    const classes = cx('BoxSelectItem', className, getPropClassesFromInstance(this), {
      'BoxSelectItem--border': border,
    });

    return (
      <ClickEventTrigger eventElementName={eventElementName}>
        <Link to={to} className={classes} onClick={action}>
          <ImageWrapper size="xsmall" className="BoxSelectItem-icon">
            {this.renderIconOrGlyph()}
          </ImageWrapper>
          {label && <h3 className="BoxSelectItem-label">{label}</h3>}
          {children && this.renderChildren()}
        </Link>
      </ClickEventTrigger>
    );
  }
}

export default BoxSelectItem;
